import { useState, useEffect, useRef } from "react";
import { apiPost, apiGet } from "../../lib/api";
import { Button } from "../ui/Button";
import { PaymentModal } from "./PaymentModal";
import type { CryptoInvoice as Invoice } from "./types";

export default function CryptoPayButton({
  planId,
  customerEmail,
  onConfirmed,
}: {
  planId: string;
  customerEmail: string;
  onConfirmed?: (token: string) => void;
}) {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function startInvoice() {
    setError(null);
    setCreating(true);
    try {
      const { invoice } = await apiPost<{ invoice: Invoice }>("/api/billing/crypto/invoice", {
        planId,
        customerEmail,
      });
      setInvoice(invoice);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCreating(false);
    }
  }

  useEffect(() => {
    if (!invoice || invoice.status !== "pending") return;
    pollRef.current = setInterval(async () => {
      try {
        const { invoice: updated } = await apiGet<{ invoice: Invoice }>(
          `/api/billing/crypto/invoice/${invoice.id}/status`
        );
        setInvoice(updated);
        if (updated.status === "confirmed" && updated.token) {
          onConfirmed?.(updated.token);
        }
        if (updated.status !== "pending" && pollRef.current) clearInterval(pollRef.current);
      } catch {
        // transient errors polling the chain shouldn't kill the flow — just try again next tick
      }
    }, 8_000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [invoice, onConfirmed]);

  function handleTriggerClick() {
    setModalOpen(true);
    if (!invoice) startInvoice();
  }

  const isDone = invoice?.state === "entitlement_granted" || invoice?.status === "confirmed";
  const triggerLabel = !invoice
    ? creating
      ? "Creating invoice…"
      : "Pay with Bitcoin"
    : isDone
    ? "Payment confirmed"
    : "View payment details";

  return (
    <>
      <Button variant="secondary" onClick={handleTriggerClick} disabled={creating}>
        {triggerLabel}
      </Button>

      {/* Closing this only hides it — invoice creation and the polling effect above are unaffected. */}
      <PaymentModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        invoice={invoice}
        creating={creating}
        error={error}
        onRetry={startInvoice}
      />
    </>
  );
}
